<template>
  <div class="fee-recon-full-report">
    <div class="no-print control-bar">
      <div class="d-flex align-items-center">
        <h4 class="mb-0">Fee Reconciliation Report</h4>
      </div>
      <div class="action-buttons">
        <button class="btn-modern btn-print" @click="print">
          <feather-icon icon="PrinterIcon" size="18" class="mr-50" />
          <span>Print Report</span>
        </button>
        <button class="btn-modern btn-close" @click="close">
          <feather-icon icon="XIcon" size="18" class="mr-50" />
          <span>Close</span>
        </button>
      </div>
    </div>

    <div v-if="loading" class="text-center py-5">
      <b-spinner variant="primary" style="width: 3.5rem; height: 3.5rem;" />
      <p class="mt-3 font-weight-bold">Loading reconciliation report...</p>
    </div>

    <div v-else-if="error" class="text-center py-5">
      <feather-icon icon="AlertCircleIcon" size="48" class="text-danger mb-3" />
      <h4 class="text-danger font-weight-bold">{{ error }}</h4>
      <b-button variant="primary" @click="loadReportData" class="mt-3">Retry</b-button>
    </div>

    <div v-else class="report-page">
      <div v-if="hasReportData" class="table-container">
        <table class="reconciliation-report-table">
          <colgroup>
            <col class="col-w-month" />
            <col class="col-w-date" />
            <col class="col-w-last-balance" />
            <template v-for="column in actualFeeColumns">
              <col :key="`${column.key}-s`" class="col-w-count" />
              <col
                :key="`${column.key}-a`"
                :class="column.key === 'tuition' ? 'col-w-tuition' : 'col-w-amount'"
              />
            </template>
            <col class="col-w-total" />
            <col class="col-w-count" />
            <col class="col-w-amount" />
            <col class="col-w-count" />
            <col class="col-w-amount" />
            <col class="col-w-count" />
            <col class="col-w-amount" />
            <col class="col-w-received" />
            <col class="col-w-balance" />
          </colgroup>
          <thead>
            <tr>
              <th :colspan="reconciliationColumnCount" class="report-main-title">
                RECONCILIATION REPORT
              </th>
            </tr>
            <tr class="header-row-main">
              <th rowspan="2" class="col-month">MONTH</th>
              <th rowspan="2" class="col-date">DATE</th>
              <th rowspan="2" class="rotated-th">
                <span class="rotated-label">LAST<br />BALANCE</span>
              </th>
              <th :colspan="actualFeeSubColumnCount" class="actual-fee-group-title">
                ACTUAL FEE
                <div class="actual-fee-subtitle">
                  Tuition · Admission · Registration · Exam · Board Exam · Late · Other
                </div>
              </th>
              <th rowspan="2" class="rotated-th highlight-col">
                <span class="rotated-label">TOTAL</span>
              </th>
              <th rowspan="2" class="rotated-th">
                <span class="rotated-label">LEFT<br />STD</span>
              </th>
              <th rowspan="2" class="rotated-th">
                <span class="rotated-label">LEFT<br />AMT</span>
              </th>
              <th rowspan="2" class="rotated-th">
                <span class="rotated-label">DISC<br />STD</span>
              </th>
              <th rowspan="2" class="rotated-th">
                <span class="rotated-label">DISC<br />AMT</span>
              </th>
              <th rowspan="2" class="rotated-th">
                <span class="rotated-label">EXMP<br />STD</span>
              </th>
              <th rowspan="2" class="rotated-th">
                <span class="rotated-label">EXMP<br />AMT</span>
              </th>
              <th rowspan="2" class="rotated-th highlight-col">
                <span class="rotated-label">RECEIVED</span>
              </th>
              <th rowspan="2" class="rotated-th highlight-col">
                <span class="rotated-label">BALANCE</span>
              </th>
            </tr>
            <tr class="header-row-sub">
              <template v-for="column in actualFeeColumns">
                <th :key="`${column.key}-students`" class="rotated-th narrow-header">
                  <span class="rotated-label">STD</span>
                </th>
                <th :key="`${column.key}-amount`" class="rotated-th narrow-header">
                  <span class="rotated-label">{{ column.shortLabel }}</span>
                </th>
              </template>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(row, rowIndex) in reconciliationRows"
              :key="`recon-row-${rowIndex}`"
              class="data-row"
            >
              <td class="text-center month-cell">
                <span class="month-stack">
                  <span class="month-stack__name">{{ row.monthName }}</span>
                  <span class="month-stack__year">{{ row.monthYear }}</span>
                </span>
              </td>
              <td class="text-center date-cell">
                <span class="date-stack">
                  <span class="date-stack__from">{{ row.dateFrom }}</span>
                  <span v-if="row.dateTo" class="date-stack__to">{{ row.dateTo }}</span>
                </span>
              </td>
              <td class="text-right amount-cell">{{ formatAmount(row.prevBalance) }}</td>
              <template v-for="column in actualFeeColumns">
                <td :key="`${column.key}-${rowIndex}-students`" class="text-center count-cell">
                  {{
                    formatCount(
                      row.feeColumns[column.key].studentCount,
                      column.key
                    )
                  }}
                </td>
                <td :key="`${column.key}-${rowIndex}-amount`" class="text-right amount-cell">
                  {{ formatAmount(row.feeColumns[column.key].amount) }}
                </td>
              </template>
              <td class="text-right amount-cell total-cell">{{ formatAmount(row.dues) }}</td>
              <td class="text-center count-cell">{{ formatCount(row.badDebtsCount) }}</td>
              <td class="text-right amount-cell">{{ formatAmount(row.badDebts) }}</td>
              <td class="text-center count-cell">{{ formatCount(row.discountCount) }}</td>
              <td class="text-right amount-cell">{{ formatAmount(row.discount) }}</td>
              <td class="text-center count-cell">{{ formatCount(row.exemptCount) }}</td>
              <td class="text-right amount-cell">{{ formatAmount(row.exempted) }}</td>
              <td class="text-right amount-cell received-cell">{{ formatAmount(row.collection) }}</td>
              <td class="text-right amount-cell balance-cell">{{ formatAmount(row.balance) }}</td>
            </tr>
            <tr
              v-for="blankIndex in reconciliationBlankRows"
              :key="`blank-row-${blankIndex}`"
              class="blank-row"
            >
              <td v-for="col in reconciliationColumnCount" :key="`blank-${blankIndex}-${col}`">
                &nbsp;
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div v-else class="text-center py-5 no-data">
        <feather-icon icon="InboxIcon" size="32" class="text-muted mb-2" />
        <p>No reconciliation data found for the selected period</p>
      </div>
    </div>
  </div>
</template>

<script>
import { BButton, BSpinner } from 'bootstrap-vue'
import axios from 'axios'

const ACTUAL_FEE_COLUMNS = [
  { key: 'tuition', label: 'TUITION FEE', shortLabel: 'TUITION', matchers: ['tuition'] },
  { key: 'admission', label: 'ADMISSION FEE', shortLabel: 'ADMISSION', matchers: ['admission'] },
  {
    key: 'registration',
    label: 'REGISTRATION FEE',
    shortLabel: 'REG.',
    matchers: ['registration'],
  },
  {
    key: 'exam',
    label: 'EXAM FEE',
    shortLabel: 'EXAM',
    matchers: ['exam'],
    excludeMatchers: ['board'],
  },
  {
    key: 'boardExam',
    label: 'BOARD EXAM FEE',
    shortLabel: 'BOARD\nEXAM',
    matchers: [
      'board exam',
      'board examination',
      'board enrolment',
      'board enrollment',
    ],
  },
  { key: 'late', label: 'LATE FEE', shortLabel: 'LATE', matchers: ['late'] },
  {
    key: 'other',
    label: 'OTHER FEES',
    shortLabel: 'OTHER',
    matchers: [],
    isOtherBucket: true,
  },
]

const ALTERNATE_SECTION_KEYS = {
  actualFees: ['actualFees', 'actualFee'],
}

export default {
  name: 'FeeReconciliationFullPrint',
  components: { BButton, BSpinner },
  data() {
    return {
      loading: false,
      error: null,
      db: '',
      cID: 0,
      reconcilationId: 0,
      reportRecords: [],
      actualFeeColumns: ACTUAL_FEE_COLUMNS,
    }
  },
  computed: {
    hasReportData() {
      return this.reportRecords.length > 0
    },
    actualFeeSubColumnCount() {
      return this.actualFeeColumns.length * 2
    },
    reconciliationColumnCount() {
      return 3 + this.actualFeeSubColumnCount + 9
    },
    reconciliationRows() {
      return this.reportRecords.map((record) => this.buildRowFromRecord(record))
    },
    reconciliationBlankRows() {
      // Keep a couple of blank rows on screen only when few data rows exist.
      // Extra blank rows cause an empty second page in print.
      if (this.reconciliationRows.length >= 5) return 0
      return Math.max(0, 5 - this.reconciliationRows.length)
    },
  },
  created() {
    this.initReport()
  },
  methods: {
    initReport() {
      const q = this.$route.query
      this.db = q.db || this.$store.state.userData.db
      this.cID = Number(q.cID || this.$store.state.userData.cId || 0)
      this.reconcilationId = Number(q.reconcilationId || 0)

      if (!this.db || !this.cID) {
        this.error = 'Database or campus ID is missing.'
        return
      }

      this.loadReportData()
    },
    monthName(month) {
      const names = [
        '',
        'January',
        'February',
        'March',
        'April',
        'May',
        'June',
        'July',
        'August',
        'September',
        'October',
        'November',
        'December',
      ]
      return names[Number(month)] || month
    },
    getSectionGroupsFromRecord(record, sectionKey) {
      if (!record) return []
      const keys = ALTERNATE_SECTION_KEYS[sectionKey] || [sectionKey]
      for (let i = 0; i < keys.length; i += 1) {
        const value = record[keys[i]]
        if (Array.isArray(value) && value.length) return value
      }
      return []
    },
    matchesActualFeeColumn(feeTypeName, column) {
      const normalized = String(feeTypeName || '').toLowerCase().trim()
      if (!normalized) return false

      if (Array.isArray(column.excludeMatchers)) {
        const excluded = column.excludeMatchers.some((matcher) =>
          normalized.includes(String(matcher).toLowerCase())
        )
        if (excluded) return false
      }

      return column.matchers.some((matcher) =>
        normalized.includes(String(matcher).toLowerCase())
      )
    },
    buildActualFeeColumnMap(groups, record = {}) {
      const map = {}
      this.actualFeeColumns.forEach((column) => {
        map[column.key] = { studentCount: 0, amount: 0 }
      })

      const mappableColumns = this.actualFeeColumns.filter(
        (column) => column.key !== 'other' && !column.isOtherBucket
      )

      ;(groups || []).forEach((group) => {
        const feeTypeName = group.feeTypeName || group.name || ''
        const column = mappableColumns.find((item) =>
          this.matchesActualFeeColumn(feeTypeName, item)
        )
        if (!column) return

        map[column.key].studentCount += Number(group.studentCount) || 0
        map[column.key].amount += Number(group.amount) || 0
      })

      const totalGroupAmount = (groups || []).reduce(
        (sum, group) => sum + (Number(group.amount) || 0),
        0
      )
      const dues = Number(record.dues) || 0
      // Same as reconciliation modal: Other = saved actual fee total minus listed fee types.
      map.other.amount = Math.max(0, dues - totalGroupAmount)
      map.other.studentCount = 0

      return map
    },
    monthShortNames() {
      return [
        '',
        'Jan',
        'Feb',
        'Mar',
        'Apr',
        'May',
        'Jun',
        'Jul',
        'Aug',
        'Sep',
        'Oct',
        'Nov',
        'Dec',
      ]
    },
    splitMonthParts(month, year) {
      if (!month || !year) {
        return { name: '—', year: '' }
      }
      const names = this.monthShortNames()
      return {
        name: names[Number(month)] || String(month),
        year: String(year),
      }
    },
    splitDateRangeParts(datestr) {
      const label = this.compactDateLabel(datestr)
      if (!label || label === '—') {
        return { from: '—', to: '' }
      }
      const match = label.match(/^(.+?)\s*[–-]\s*(.+)$/)
      if (!match) {
        return { from: label, to: '' }
      }
      return {
        from: match[1].trim(),
        to: match[2].trim(),
      }
    },
    compactMonthLabel(month, year) {
      const parts = this.splitMonthParts(month, year)
      if (parts.name === '—') return '—'
      return `${parts.name} ${parts.year}`
    },
    compactDateLabel(datestr) {
      if (!datestr) return '—'
      return String(datestr)
        .replace(/\bJanuary\b/gi, 'Jan')
        .replace(/\bFebruary\b/gi, 'Feb')
        .replace(/\bMarch\b/gi, 'Mar')
        .replace(/\bApril\b/gi, 'Apr')
        .replace(/\bJune\b/gi, 'Jun')
        .replace(/\bJuly\b/gi, 'Jul')
        .replace(/\bAugust\b/gi, 'Aug')
        .replace(/\bSeptember\b/gi, 'Sep')
        .replace(/\bOctober\b/gi, 'Oct')
        .replace(/\bNovember\b/gi, 'Nov')
        .replace(/\bDecember\b/gi, 'Dec')
        .replace(/\s+to\s+/gi, ' – ')
    },
    buildRowFromRecord(record) {
      const monthParts = this.splitMonthParts(record.month, record.year)
      const dateParts = this.splitDateRangeParts(record.datestr)
      return {
        monthName: monthParts.name,
        monthYear: monthParts.year,
        dateFrom: dateParts.from,
        dateTo: dateParts.to,
        prevBalance: record.prevBalance,
        feeColumns:
          record.feeColumns ||
          this.buildActualFeeColumnMap([], record),
        dues: record.dues,
        badDebtsCount: record.badDebtsCount ?? 0,
        badDebts: record.badDebts,
        discountCount: record.discountCount ?? 0,
        discount: record.discount,
        exemptCount: record.exemptCount ?? 0,
        exempted: record.exempted,
        collection: record.collection,
        balance: record.balance,
      }
    },
    getReconciliationId(record) {
      return Number(record?.id || record?.reconcilationId || record?.reconId || 0)
    },
    sortReconciliationRecords(records) {
      return [...records].sort((a, b) => {
        const yearDiff = Number(a.year) - Number(b.year)
        if (yearDiff !== 0) return yearDiff
        return Number(a.month) - Number(b.month)
      })
    },
    parseListResponse(data) {
      if (Array.isArray(data)) return data
      if (Array.isArray(data?.data)) return data.data
      return []
    },
    resolveRecordFromPayload(payload) {
      if (!payload) return null

      let records = []
      if (Array.isArray(payload.data)) {
        records = payload.data
      } else if (Array.isArray(payload)) {
        records = payload
      } else if (payload.data && typeof payload.data === 'object') {
        records = [payload.data]
      } else if (payload.id) {
        records = [payload]
      }

      if (!records.length) return null

      if (this.reconcilationId) {
        const match = records.find(
          (item) => Number(item.id) === Number(this.reconcilationId)
        )
        if (match) return match
      }

      return records[0]
    },
    async loadReportData() {
      this.loading = true
      this.error = null
      try {
        const records = await this.fetchAllReportRecords()
        if (!records.length) {
          this.error = 'No reconciliation records found.'
          return
        }

        this.reportRecords = records
      } catch (err) {
        console.error(err)
        this.error = err?.message || 'Failed to retrieve reconciliation report.'
      } finally {
        this.loading = false
      }
    },
    async fetchAllReportRecords() {
      const list = this.sortReconciliationRecords(await this.fetchReconciliationList())
      if (!list.length) return []

      if (this.reconcilationId) {
        const selected = list.find(
          (item) => this.getReconciliationId(item) === this.reconcilationId
        )
        if (!selected) {
          throw new Error('Reconciliation record not found.')
        }
        return [await this.enrichRecordWithFees(selected)]
      }

      return Promise.all(list.map((item) => this.enrichRecordWithFees(item)))
    },
    async fetchReconciliationList() {
      const response = await axios.get(`${this.$store.state.domain}FeeReconcilation`, {
        params: {
          db: this.db,
          cID: this.cID,
        },
        headers: this.getAuthHeaders(),
      })

      return this.parseListResponse(response.data)
    },
    recordHasActualFeeGroups(record) {
      return this.getSectionGroupsFromRecord(record, 'actualFees').length > 0
    },
    async enrichRecordWithFees(record) {
      const reconcilationId = this.getReconciliationId(record)
      let detail = record

      const needsFeeFetch =
        reconcilationId && !this.recordHasActualFeeGroups(record)

      if (needsFeeFetch) {
        try {
          const fetched = await this.fetchReportForId(reconcilationId)
          if (fetched) {
            detail = { ...record, ...fetched }
          }
        } catch (error) {
          console.warn(`Fee breakdown unavailable for reconciliation ${reconcilationId}`, error)
        }
      }

      return {
        ...detail,
        feeColumns: this.buildActualFeeColumnMap(
          this.getSectionGroupsFromRecord(detail, 'actualFees'),
          detail
        ),
      }
    },
    getAuthHeaders() {
      return {
        Authorization: `Bearer ${this.$store.state.userData.token}`,
        accept: 'application/json',
      }
    },
    async fetchReportForId(reconcilationId) {
      const response = await axios.get(`${this.$store.state.domain}FeeReconcilation/GetReport`, {
        params: {
          db: this.db,
          cID: this.cID,
          reconcilationId,
          includeStudents: false,
          type: '',
          feeTypeID: 0,
          studentPage: 1,
          studentPageSize: 1,
        },
        headers: this.getAuthHeaders(),
      })

      const payload = response?.data
      if (!payload || payload.status !== 'success') {
        throw new Error(payload?.message || 'Unexpected response from server.')
      }

      return this.resolveRecordFromPayload(payload)
    },
    formatAmount(value) {
      if (value === null || value === undefined || value === '') return '-'
      const num = Number(value)
      if (Number.isNaN(num)) return '-'
      // Compact with commas, no decimals — keeps wide numbers readable in one line
      return Math.round(num).toLocaleString('en-US')
    },
    formatCount(value, columnKey) {
      const num = Number(value)
      if (columnKey === 'other' && (Number.isNaN(num) || num === 0)) {
        return '—'
      }
      if (Number.isNaN(num)) return '0'
      return Math.round(num).toLocaleString('en-US')
    },
    print() {
      window.print()
    },
    close() {
      window.close()
    },
  },
}
</script>

<style lang="scss" scoped>
.fee-recon-full-report {
  background: #eef1f4;
  min-height: 100vh;
  padding: 0.75rem 1rem 1.5rem;
  font-family: 'Calibri', 'Segoe UI', Tahoma, Arial, sans-serif;
  color: #111;
  width: 100%;
  box-sizing: border-box;
}

.control-bar {
  background: #fff;
  padding: 0.85rem 1.2rem;
  margin-bottom: 0.85rem;
  border-radius: 10px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.07);
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: sticky;
  top: 0.4rem;
  z-index: 100;

  h4 {
    font-weight: 700;
    font-size: 1.25rem;
    margin: 0;
  }
}

.action-buttons {
  display: flex;
  gap: 10px;
}

.btn-modern {
  display: flex;
  align-items: center;
  padding: 9px 18px;
  border-radius: 8px;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  border: none;
}

.btn-print {
  background: #2d3436;
  color: #fff;
}

.btn-close {
  background: #fff;
  color: #636e72;
  border: 1px solid #dfe6e9;
}

.report-page {
  background: #fff;
  padding: 0.85rem 0.9rem 1rem;
  border-radius: 10px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
}

.table-container {
  width: 100%;
  overflow: visible;
}

.reconciliation-report-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  border: 1px solid #9ca3af;
  table-layout: fixed;
  font-size: 13.5px;
  font-family: 'Calibri', 'Segoe UI', Tahoma, Arial, sans-serif;

  .col-w-month { width: 2.4%; }
  .col-w-date { width: 4.2%; }
  .col-w-last-balance { width: 5.8%; }
  .col-w-count { width: 2.3%; }
  .col-w-amount { width: 3.55%; }
  .col-w-tuition { width: 5.55%; }
  .col-w-total { width: 5.35%; }
  .col-w-received { width: 5.75%; }
  .col-w-balance { width: 5.75%; }

  th,
  td {
    border: 0.5px solid #9ca3af;
    padding: 0.4rem 0.22rem;
    vertical-align: middle;
    line-height: 1.2;
    box-sizing: border-box;
  }

  thead th {
    background: #f0f2f5;
    font-weight: 700;
    color: #111;
  }

  .report-main-title {
    text-align: center;
    font-size: 28px;
    font-weight: 800;
    letter-spacing: 1.2px;
    padding: 14px 8px;
    background: #fff;
    border-bottom: 1px solid #9ca3af;
    font-family: 'Calibri', 'Segoe UI', Tahoma, Arial, sans-serif;
  }

  .actual-fee-group-title {
    text-align: center;
    font-size: 14px;
    font-weight: 800;
    padding: 8px 4px;
    background: #e6e9ed;
    white-space: normal;
  }

  .actual-fee-subtitle {
    margin-top: 4px;
    font-size: 10.5px;
    font-weight: 600;
    line-height: 1.3;
    color: #333;
  }

  .col-month,
  .col-date {
    text-align: center;
    font-size: 11px;
    font-weight: 800;
    writing-mode: horizontal-tb;
    transform: none;
    height: auto;
    padding: 6px 2px;
    background: #f0f2f5;
  }

  /* Keep borders on the th; rotate only the inner label so column lines stay visible */
  .rotated-th {
    height: 128px;
    min-width: 0;
    padding: 4px 2px;
    overflow: hidden;
    position: relative;
    background: #f0f2f5;
    text-align: center;
    vertical-align: middle;
  }

  .rotated-label {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    writing-mode: vertical-rl;
    transform: rotate(180deg);
    white-space: pre-line;
    text-align: center;
    font-size: 11px;
    font-weight: 700;
    line-height: 1.15;
    height: 118px;
    max-height: 118px;
  }

  .narrow-header {
    height: 108px;
    background: #e9edf2;
  }

  .narrow-header .rotated-label {
    font-size: 10.5px;
    height: 98px;
    max-height: 98px;
  }

  .highlight-col {
    background: #e2e6eb !important;
  }

  tbody td {
    font-size: 12.5px;
    background: #fff;
  }

  .data-row:nth-child(even) td {
    background: #f7f8fa;
  }

  .month-cell,
  .date-cell {
    overflow: hidden;
    padding: 0.28rem 0.05rem;
  }

  .month-stack,
  .date-stack {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 1px;
    line-height: 1.12;
    max-width: 100%;
  }

  .month-stack__name {
    font-weight: 800;
    font-size: 11px;
    white-space: nowrap;
  }

  .month-stack__year {
    font-weight: 700;
    font-size: 9.5px;
    color: #374151;
    white-space: nowrap;
  }

  .date-stack__from,
  .date-stack__to {
    display: block;
    font-weight: 700;
    font-size: 8.5px;
    white-space: normal;
    word-break: break-word;
    overflow-wrap: anywhere;
    text-align: center;
    max-width: 100%;
  }

  .date-stack__to {
    font-weight: 600;
    color: #4b5563;
  }

  .count-cell {
    font-weight: 700;
    color: #111827;
    font-size: 12px;
    white-space: nowrap;
  }

  .amount-cell {
    font-variant-numeric: tabular-nums;
    font-feature-settings: 'tnum' 1;
    font-weight: 700;
    font-size: 11.5px;
    white-space: nowrap;
    overflow: visible;
    word-break: normal;
    overflow-wrap: normal;
    padding-left: 0.18rem;
    padding-right: 0.28rem;
  }

  .total-cell,
  .received-cell,
  .balance-cell {
    font-weight: 800;
    font-size: 12px;
    background: #f0f2f5 !important;
  }

  .received-cell { color: #166534; }
  .balance-cell { color: #991b1b; }

  .blank-row td {
    height: 40px;
    background: #fff !important;
  }
}

.no-data {
  color: #636e72;
  font-weight: 600;
}

@media print {
  @page {
    size: A4 landscape;
    margin: 4mm;
  }

  * {
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
    color-adjust: exact !important;
  }

  html,
  body {
    width: 100% !important;
    height: auto !important;
    margin: 0 !important;
    padding: 0 !important;
    background: #fff !important;
    overflow: hidden !important;
  }

  .fee-recon-full-report {
    background: #fff !important;
    padding: 0 !important;
    margin: 0 !important;
    width: 100% !important;
    min-height: 0 !important;
    height: auto !important;
    overflow: hidden !important;
    page-break-after: avoid !important;
    break-after: avoid-page !important;

    .no-print { display: none !important; }

    .report-page {
      padding: 0 !important;
      margin: 0 !important;
      box-shadow: none !important;
      border-radius: 0 !important;
      width: 100% !important;
      min-height: 0 !important;
      page-break-after: avoid !important;
      break-after: avoid-page !important;
    }

    .table-container {
      width: 100% !important;
      overflow: hidden !important;
      margin: 0 !important;
      padding: 0 !important;
    }

    .reconciliation-report-table {
      width: 100% !important;
      font-size: 10px !important;
      font-family: 'Calibri', 'Segoe UI', Tahoma, Arial, sans-serif !important;
      border-collapse: separate !important;
      border-spacing: 0 !important;
      border: 1px solid #6b7280 !important;
      page-break-inside: auto;
      page-break-after: avoid !important;
      break-after: avoid-page !important;
      margin: 0 !important;

      .col-w-month { width: 2.4% !important; }
      .col-w-date { width: 4.2% !important; }
      .col-w-last-balance { width: 5.8% !important; }
      .col-w-count { width: 2.3% !important; }
      .col-w-amount { width: 3.55% !important; }
      .col-w-tuition { width: 5.55% !important; }
      .col-w-total { width: 5.35% !important; }
      .col-w-received { width: 5.75% !important; }
      .col-w-balance { width: 5.75% !important; }

      th,
      td {
        padding: 3px 2px !important;
        border: 0.5px solid #6b7280 !important;
        background: #fff !important;
        color: #000 !important;
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
      }

      thead th {
        background: #f0f0f0 !important;
        font-weight: 800 !important;
        border: 0.5px solid #6b7280 !important;
      }

      .report-main-title {
        font-size: 16px !important;
        padding: 6px 4px !important;
        border: 0.5px solid #6b7280 !important;
        border-bottom: 1px solid #6b7280 !important;
        background: #fff !important;
        letter-spacing: 0.5px !important;
      }

      .actual-fee-group-title {
        font-size: 10px !important;
        padding: 4px 2px !important;
        background: #ececec !important;
        border: 0.5px solid #6b7280 !important;
      }

      .actual-fee-subtitle {
        font-size: 7px !important;
        margin-top: 2px !important;
      }

      .col-month,
      .col-date {
        font-size: 9px !important;
        padding: 3px 2px !important;
        background: #f5f5f5 !important;
      }

      .rotated-th {
        height: 78px !important;
        padding: 2px 1px !important;
        overflow: hidden !important;
        background: #f0f0f0 !important;
        text-align: center !important;
        vertical-align: middle !important;
      }

      .rotated-label {
        display: inline-flex !important;
        align-items: center !important;
        justify-content: center !important;
        font-size: 8px !important;
        height: 70px !important;
        max-height: 70px !important;
      }

      .narrow-header {
        height: 68px !important;
        background: #ececec !important;
      }

      .narrow-header .rotated-label {
        font-size: 7.5px !important;
        height: 60px !important;
        max-height: 60px !important;
      }

      .highlight-col {
        background: #e8e8e8 !important;
      }

      .month-cell,
      .date-cell {
        overflow: hidden !important;
        padding: 2px 1px !important;
      }

      .month-stack__name {
        font-size: 8px !important;
      }

      .month-stack__year {
        font-size: 7px !important;
      }

      .date-stack__from,
      .date-stack__to {
        font-size: 6.5px !important;
        line-height: 1.1 !important;
      }

      .count-cell {
        font-size: 8.5px !important;
        white-space: nowrap !important;
        padding: 3px 2px !important;
      }

      .amount-cell {
        font-size: 8.5px !important;
        white-space: nowrap !important;
        word-break: keep-all !important;
        overflow-wrap: normal !important;
        padding: 3px 2px !important;
      }

      .total-cell,
      .received-cell,
      .balance-cell {
        font-size: 8.5px !important;
        background: #f5f5f5 !important;
        padding: 3px 2px !important;
      }

      .received-cell { color: #0b5d1e !important; }
      .balance-cell { color: #8a1010 !important; }

      .data-row:nth-child(even) td {
        background: #fafafa !important;
      }

      .blank-row {
        display: none !important;
      }
    }
  }
}
</style>
